import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useForm, Controller } from 'react-hook-form';
import { BUTTON_BORDER_RADIUS, TEXTFIELD_BORDER_RADIUS } from 'constants/borderRadius';

/**
 * @typedef {object} MFAVerificationProps
 * @property {string} phoneNumber - Masked phone number for display
 * @property {Function} onSubmit - Callback when code is submitted
 * @property {Function} onResend - Callback to resend verification code
 * @property {boolean} [isLoading=false] - Shows loading state
 * @property {boolean} [isResending=false] - Shows resend loading state
 * @property {string} [errorMessage] - Error to display
 * @property {Function} [onBack] - Optional callback to go back to email step
 * @property {Function} [onClearError] - Callback to clear external error when user types
 */

/**
 * MFA verification code entry step.
 * Allows users to enter the 6-digit SMS code.
 *
 * @param {MFAVerificationProps} props - Component props
 * @returns {React.ReactElement}
 */
const MFAVerification = ({
    phoneNumber,
    onSubmit,
    onResend,
    isLoading = false,
    isResending = false,
    errorMessage,
    onBack,
    onClearError,
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            code: '',
        },
    });

    /**
     * Handles form submission
     * @param {{code: string}} data - Form data
     */
    const onFormSubmit = (data) => {
        onSubmit(data.code.trim());
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            sx={{ mt: 2 }}
        >
            <Alert severity="info" sx={{ mb: 3 }}>
                A verification code was sent to <strong>{phoneNumber}</strong>.
                Please enter it below.
            </Alert>

            <Controller
                name="code"
                control={control}
                rules={{
                    required: 'Verification code is required',
                    pattern: {
                        value: /^\d{6}$/,
                        message: 'Please enter a 6-digit code',
                    },
                }}
                render={({ field }) => (
                    <TextField
                        {...field}
                        onChange={(e) => {
                            field.onChange(e);
                            // Clear external error when user starts typing
                            if (errorMessage && onClearError) {
                                onClearError();
                            }
                        }}
                        id="mfa-code-input"
                        label="Verification Code"
                        placeholder="123456"
                        fullWidth
                        autoComplete="one-time-code"
                        autoFocus
                        disabled={isLoading}
                        error={!!errors.code || !!errorMessage}
                        helperText={errors.code?.message || errorMessage || ' '}
                        slotProps={{
                            htmlInput: {
                                maxLength: 6,
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                style: {
                                    textAlign: 'center',
                                    letterSpacing: '0.5em',
                                    fontSize: '1.5rem',
                                    fontWeight: 600,
                                },
                            },
                        }}
                        sx={{
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: TEXTFIELD_BORDER_RADIUS,
                            },
                        }}
                    />
                )}
            />

            <Button
                id="mfa-verify-btn"
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={isLoading}
                startIcon={
                    isLoading
                        ? <CircularProgress size={20} color="inherit" />
                        : <VerifiedUserIcon />
                }
                sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
            >
                {isLoading ? 'Verifying...' : 'Verify'}
            </Button>

            {/* Resend Code */}
            <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Button
                    id="mfa-resend-btn"
                    variant="outlined"
                    size='large'
                    fullWidth
                    color="primary"
                    onClick={onResend}
                    disabled={isLoading || isResending}
                    startIcon={
                        isResending
                            ? <CircularProgress size={16} color="inherit" />
                            : <RefreshIcon />
                    }
                    sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
                >
                    {isResending ? 'Sending...' : 'Resend'}
                </Button>
            </Box>

            {/* Back to email link */}
            {onBack && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={onBack}
                        disabled={isLoading}
                        sx={{ cursor: 'pointer' }}
                    >
                        Start over
                    </Link>
                </Box>
            )}
        </Box>
    );
};

MFAVerification.propTypes = {
    phoneNumber: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onResend: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    isResending: PropTypes.bool,
    errorMessage: PropTypes.string,
    onBack: PropTypes.func,
    onClearError: PropTypes.func,
};

export default MFAVerification;
